// AGC: VoiceCalibration — Multi-step voice calibration wizard
// AGC: Steps: Intro → Noise Baseline → Phrase Recording (x10) → Results
// AGC: Uses WER as primary metric, per-domain overlays, quality gates.
// AGC: Saves as candidate profile with promote workflow.

import React, { useState, useCallback, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Mic, MicOff, Volume2, CheckCircle2, XCircle, AlertTriangle,
  ChevronRight, ArrowLeft, RotateCcw, Save, Loader2,
  Shield, BarChart3, Zap,
} from 'lucide-react'
import { TopBar } from '../shared'
import { useTranslation } from '../../hooks/useTranslation'
import { useVoiceCalibration } from '../../hooks/useVoiceCalibration'
import { useSubscription } from '../../context/SubscriptionContext'
import type { CalibrationPhraseResult } from '../../hooks/useVoiceCalibration'

// ─────────────────────────────────────────────────────────
// AGC: Props
// ─────────────────────────────────────────────────────────

interface VoiceCalibrationProps {
  onComplete: () => void
  onSkip?: () => void
  onBack?: () => void
  isPostWizard?: boolean
}

// ─────────────────────────────────────────────────────────
// AGC: Animation variants
// ─────────────────────────────────────────────────────────

const slideVariants = {
  enter: { opacity: 0, x: 30 },
  center: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -30 },
}

// ─────────────────────────────────────────────────────────
// AGC: Step components
// ─────────────────────────────────────────────────────────

/** AGC: Step 0 — Intro */
const IntroStep: React.FC<{
  onStart: () => void
  onSkip?: () => void
  isPostWizard?: boolean
  lastCalibration: string | null
}> = ({ onStart, onSkip, isPostWizard, lastCalibration }) => {
  const { t } = useTranslation()

  return (
    <motion.div
      variants={slideVariants}
      initial="enter" animate="center" exit="exit"
      transition={{ duration: 0.3 }}
      className="flex flex-col items-center justify-center px-6 min-h-[80vh] text-center"
    >
      <div className="w-28 h-28 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center mb-8">
        <Mic className="w-14 h-14 text-purple-600 dark:text-purple-400" />
      </div>

      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
        {t('voice.calibration.title')}
      </h2>

      <p className="text-base text-gray-600 dark:text-gray-400 mb-3 max-w-md leading-relaxed">
        {t('voice.calibration.intro')}
      </p>

      <p className="text-sm text-gray-500 dark:text-gray-500 mb-8 max-w-md">
        {t('voice.calibration.introDescription')}
      </p>

      {lastCalibration && (
        <p className="text-sm text-emerald-600 dark:text-emerald-400 mb-6">
          {t('voice.calibration.lastCalibration')}: {new Date(lastCalibration).toLocaleDateString()}
        </p>
      )}

      {/* AGC: Tips */}
      <div className="w-full max-w-md bg-amber-50 dark:bg-amber-900/20 rounded-xl p-5 mb-8 text-left">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
          <span className="text-sm font-semibold text-amber-700 dark:text-amber-300">
            {t('voice.calibration.tips')}
          </span>
        </div>
        <ul className="text-sm text-amber-700 dark:text-amber-400 space-y-2">
          <li>• {t('voice.calibration.tipQuiet')}</li>
          <li>• {t('voice.calibration.tipSteady')}</li>
          <li>• {t('voice.calibration.tipClear')}</li>
        </ul>
      </div>

      <button
        onClick={onStart}
        className="w-full max-w-md py-3.5 bg-purple-600 hover:bg-purple-700 text-white text-base font-semibold rounded-xl transition-colors"
      >
        {t('voice.calibration.startButton')}
      </button>

      {onSkip && (
        <button
          onClick={onSkip}
          className="mt-4 w-full max-w-md py-3.5 text-base font-semibold text-gray-700 dark:text-gray-200 border-2 border-gray-300 dark:border-gray-500 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-all"
        >
          {t('voice.calibration.skipButton')}
        </button>
      )}
    </motion.div>
  )
}

/** AGC: Step 1 — Noise Baseline */
const NoiseStep: React.FC<{
  isRecording: boolean
  noiseLevel: string | null
  onRecord: () => void
  onNext: () => void
  onSkipAll?: () => void
}> = ({ isRecording, noiseLevel, onRecord, onNext, onSkipAll }) => {
  const { t } = useTranslation()

  const levelColors: Record<string, string> = {
    excellent: 'text-emerald-600 bg-emerald-100 dark:text-emerald-400 dark:bg-emerald-900/30',
    good: 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/30',
    fair: 'text-amber-600 bg-amber-100 dark:text-amber-400 dark:bg-amber-900/30',
    poor: 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900/30',
  }

  return (
    <motion.div
      variants={slideVariants}
      initial="enter" animate="center" exit="exit"
      transition={{ duration: 0.3 }}
      className="flex flex-col items-center px-6 py-8"
    >
      <div className="w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mb-4">
        <Volume2 className="w-8 h-8 text-blue-600 dark:text-blue-400" />
      </div>

      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
        {t('voice.calibration.noiseStep')}
      </h3>

      <p className="text-sm text-gray-600 dark:text-gray-400 mb-6 text-center max-w-sm">
        {t('voice.calibration.noiseInstruction')}
      </p>

      {!noiseLevel ? (
        <button
          onClick={onRecord}
          disabled={isRecording}
          className={`w-24 h-24 rounded-full flex items-center justify-center transition-all ${
            isRecording
              ? 'bg-red-500 animate-pulse scale-110'
              : 'bg-blue-600 hover:bg-blue-700'
          }`}
        >
          {isRecording ? (
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
          ) : (
            <Volume2 className="w-10 h-10 text-white" />
          )}
        </button>
      ) : (
        <div className={`px-6 py-3 rounded-xl font-semibold ${levelColors[noiseLevel] || levelColors.fair}`}>
          {t(`voice.calibration.${noiseLevel}`)}
        </div>
      )}

      {noiseLevel === 'poor' && (
        <p className="text-xs text-red-500 dark:text-red-400 mt-3 text-center max-w-sm">
          {t('voice.calibration.noisePoor')}
        </p>
      )}

      {noiseLevel && (
        <button
          onClick={onNext}
          className="mt-6 px-8 py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-xl transition-colors flex items-center gap-2"
        >
          {t('common.next')} <ChevronRight className="w-4 h-4" />
        </button>
      )}

      {/* AGC: Skip all — exits wizard entirely, available when not post-wizard mandatory */}
      {onSkipAll && (
        <button
          onClick={onSkipAll}
          className="mt-4 px-5 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-all"
        >
          {t('voice.calibration.skipAllButton')}
        </button>
      )}
    </motion.div>
  )
}

/** AGC: Step 2 — Phrase Recording with teaching text */
const PhraseStep: React.FC<{
  phrase: { text: string; domain: string; category?: string; teaching?: string; index: number } | null
  phraseIndex: number
  totalPhrases: number
  isRecording: boolean
  isProcessing: boolean
  lastResult: CalibrationPhraseResult | null
  showResult: boolean
  onStartRecording: () => void
  onStopRecording: () => void
  onNext: () => void
  onRetry: () => void
  onSkipAll?: () => void
}> = ({ phrase, phraseIndex, totalPhrases, isRecording, isProcessing, lastResult, showResult, onStartRecording, onStopRecording, onNext, onRetry, onSkipAll }) => {
  const { t } = useTranslation()

  if (!phrase) return null

  const categoryLabels: Record<string, { label: string; icon: string; color: string }> = {
    sell: { label: 'Venda', icon: '💵', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
    buy: { label: 'Compra', icon: '🛒', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
    expense: { label: 'Despesa', icon: '💰', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
    lot: { label: 'Talhão', icon: '🌾', color: 'bg-lime-100 text-lime-700 dark:bg-lime-900/30 dark:text-lime-400' },
    machine: { label: 'Máquina', icon: '🚜', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
    task: { label: 'Tarefa', icon: '📋', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
    inventory: { label: 'Estoque', icon: '📦', color: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400' },
  }

  const cat = categoryLabels[phrase.category || ''] || { label: phrase.domain, icon: '🎤', color: 'bg-gray-100 text-gray-700' }

  return (
    <motion.div
      key={phraseIndex}
      variants={slideVariants}
      initial="enter" animate="center" exit="exit"
      transition={{ duration: 0.3 }}
      className="flex flex-col items-center px-6 py-4"
    >
      {/* AGC: Progress bar */}
      <div className="w-full max-w-sm mb-3">
        <div className="flex justify-between items-center text-xs text-gray-500 dark:text-gray-400 mb-1">
          <span>{t('voice.calibration.phraseStep', { current: phraseIndex + 1, total: totalPhrases })}</span>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cat.color}`}>
            {cat.icon} {cat.label}
          </span>
        </div>
        <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div
            className="h-full bg-purple-600 transition-all duration-300 rounded-full"
            style={{ width: `${((phraseIndex + (showResult ? 1 : 0)) / totalPhrases) * 100}%` }}
          />
        </div>
      </div>

      {/* AGC: Teaching explanation — multi-line with \n support */}
      {phrase.teaching && !showResult && (
        <div className="w-full max-w-sm bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-3 mb-3">
          {phrase.teaching.split('\n').map((line, i) => (
            <p key={i} className="text-xs text-blue-700 dark:text-blue-300 leading-relaxed">
              {i === 0 ? '' : ''}{line}
            </p>
          ))}
        </div>
      )}

      {/* AGC: Phrase text to say */}
      <div className="w-full max-w-sm bg-gray-50 dark:bg-gray-800 rounded-xl p-4 mb-4 text-center">
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
          {t('voice.calibration.phraseInstruction')}
        </p>
        <p className="text-base font-medium text-gray-900 dark:text-white leading-relaxed">
          &ldquo;{phrase.text}&rdquo;
        </p>
      </div>

      {/* AGC: Recording button or result */}
      {!showResult ? (
        <div className="flex flex-col items-center">
          {isProcessing ? (
            <div className="w-20 h-20 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-purple-600 dark:text-purple-400 animate-spin" />
            </div>
          ) : (
            <>
              {/* AGC: Red annotation above mic when recording */}
              {isRecording && (
                <p className="text-sm font-semibold text-red-500 mb-2 animate-pulse">
                  {t('voice.calibration.tapToSend')}
                </p>
              )}
              <button
                onClick={isRecording ? onStopRecording : onStartRecording}
                className={`w-20 h-20 rounded-full flex items-center justify-center transition-all ${
                  isRecording
                    ? 'bg-red-500 scale-110 animate-pulse'
                    : 'bg-purple-600 hover:bg-purple-700 active:scale-95'
                }`}
              >
                {isRecording ? (
                  <MicOff className="w-8 h-8 text-white" />
                ) : (
                  <Mic className="w-8 h-8 text-white" />
                )}
              </button>
            </>
          )}

          <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
            {isRecording
              ? t('voice.calibration.recording')
              : isProcessing
                ? t('voice.calibration.processing')
                : t('voice.calibration.tapToRecord')}
          </p>
        </div>
      ) : lastResult && (
        <PhraseResultCard result={lastResult} onNext={onNext} onRetry={onRetry} />
      )}

      {/* AGC: Skip all — only shown while recording (not during result card) */}
      {!showResult && onSkipAll && (
        <button
          onClick={onSkipAll}
          className="mt-4 px-5 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-all"
        >
          {t('voice.calibration.skipAllButton')}
        </button>
      )}
    </motion.div>
  )
}

/** AGC: Phrase result card with WER breakdown */
const PhraseResultCard: React.FC<{
  result: CalibrationPhraseResult
  onNext: () => void
  onRetry: () => void
}> = ({ result, onNext, onRetry }) => {
  const { t } = useTranslation()
  const accuracy = Math.round((1 - result.wer.wer) * 100)
  const isGood = accuracy >= 70
  const isRejected = result.rejected

  return (
    <div className="w-full max-w-sm">
      {/* AGC: Overall match */}
      <div className={`rounded-xl p-4 mb-3 ${
        isRejected
          ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
          : isGood
            ? 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800'
            : 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800'
      }`}>
        <div className="flex items-center gap-2 mb-2">
          {isRejected ? (
            <XCircle className="w-5 h-5 text-red-500" />
          ) : isGood ? (
            <CheckCircle2 className="w-5 h-5 text-emerald-500" />
          ) : (
            <AlertTriangle className="w-5 h-5 text-amber-500" />
          )}
          <span className={`text-sm font-semibold ${
            isRejected ? 'text-red-700 dark:text-red-300' : isGood ? 'text-emerald-700 dark:text-emerald-300' : 'text-amber-700 dark:text-amber-300'
          }`}>
            {isRejected
              ? t('voice.calibration.qualityTooLow')
              : isGood
                ? t('voice.calibration.phraseMatch')
                : t('voice.calibration.phraseMismatch')}
            {' '}({accuracy}%)
          </span>
        </div>

        {/* AGC: Transcription comparison */}
        <div className="text-xs space-y-1">
          <div>
            <span className="text-gray-500 dark:text-gray-400">{t('voice.calibration.expected')}: </span>
            <span className="text-gray-700 dark:text-gray-300">{result.expectedText}</span>
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">{t('voice.calibration.heard')}: </span>
            <span className="text-gray-700 dark:text-gray-300">{result.transcribedText || '—'}</span>
          </div>
        </div>

        {/* AGC: WER breakdown */}
        {!isRejected && result.wer.wer > 0 && (
          <div className="flex gap-3 mt-2 text-xs">
            {result.wer.substitutions > 0 && (
              <span className="text-orange-600 dark:text-orange-400">
                {t('voice.calibration.substitution')}: {result.wer.substitutions}
              </span>
            )}
            {result.wer.deletions > 0 && (
              <span className="text-yellow-600 dark:text-yellow-400">
                {t('voice.calibration.deletion')}: {result.wer.deletions}
              </span>
            )}
            {result.wer.insertions > 0 && (
              <span className="text-red-600 dark:text-red-400">
                {t('voice.calibration.insertion')}: {result.wer.insertions}
              </span>
            )}
          </div>
        )}

        {/* AGC: Slot accuracy checkmarks */}
        {!isRejected && result.slotAccuracy?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {result.slotAccuracy.map((slot, idx) => (
              <span
                key={idx}
                className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${
                  slot.correct
                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                    : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                }`}
              >
                {slot.correct ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                {slot.slotType}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* AGC: Action buttons */}
      <div className="flex gap-3">
        {isRejected && (
          <button
            onClick={onRetry}
            className="flex-1 py-2.5 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-xl flex items-center justify-center gap-2"
          >
            <RotateCcw className="w-4 h-4" /> {t('voice.calibration.retry')}
          </button>
        )}
        <button
          onClick={onNext}
          className="flex-1 py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-xl flex items-center justify-center gap-2"
        >
          {t('common.next')} <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}

/** AGC: Step 3 — Results dashboard */
const ResultsStep: React.FC<{
  phraseResults: CalibrationPhraseResult[]
  acceptedCount: number
  rejectedCount: number
  noiseLevel: string | null
  saveResult: { success: boolean; promoted: boolean; comparison: { action: string; reason: string; werDelta: number }; profile: { overallWER: number; overallSlotAccuracy: number; qualityGatePassed: boolean } } | null
  isSaving: boolean
  onSave: () => void
  onForcePromote: () => void
  onComplete: () => void
}> = ({ phraseResults, acceptedCount, rejectedCount, noiseLevel, saveResult, isSaving, onSave, onForcePromote, onComplete }) => {
  const { t } = useTranslation()

  const accepted = phraseResults.filter(r => !r.rejected)
  const avgWER = accepted.length > 0
    ? accepted.reduce((sum, r) => sum + r.wer.wer, 0) / accepted.length
    : 1
  const accuracy = Math.round((1 - avgWER) * 100)
  const qualityGateFailed = rejectedCount > 3

  // AGC: Domain breakdown
  const domains = new Map<string, { count: number; totalWER: number }>()
  for (const r of accepted) {
    const existing = domains.get(r.domain) || { count: 0, totalWER: 0 }
    existing.count++
    existing.totalWER += r.wer.wer
    domains.set(r.domain, existing)
  }

  return (
    <motion.div
      variants={slideVariants}
      initial="enter" animate="center" exit="exit"
      transition={{ duration: 0.3 }}
      className="flex flex-col items-center px-6 py-6"
    >
      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
        {t('voice.calibration.resultsTitle')}
      </h3>

      {/* AGC: Overall accuracy gauge */}
      <div className="relative w-32 h-32 mb-4">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 120 120">
          <circle cx="60" cy="60" r="52" fill="none" stroke="currentColor" strokeWidth="8" className="text-gray-200 dark:text-gray-700" />
          <circle
            cx="60" cy="60" r="52" fill="none" strokeWidth="8" strokeLinecap="round"
            strokeDasharray={`${(accuracy / 100) * 327} 327`}
            className={accuracy >= 80 ? 'text-emerald-500' : accuracy >= 60 ? 'text-amber-500' : 'text-red-500'}
            stroke="currentColor"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-bold text-gray-900 dark:text-white">{accuracy}%</span>
          <span className="text-xs text-gray-500 dark:text-gray-400">{t('voice.calibration.accuracy')}</span>
        </div>
      </div>

      {/* AGC: Stats row */}
      <div className="grid grid-cols-3 gap-3 w-full max-w-sm mb-4">
        <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3 text-center">
          <BarChart3 className="w-4 h-4 mx-auto mb-1 text-purple-500" />
          <p className="text-xs text-gray-500 dark:text-gray-400">{t('voice.calibration.phrases')}</p>
          <p className="text-sm font-bold text-gray-900 dark:text-white">{acceptedCount}/{phraseResults.length}</p>
        </div>
        <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3 text-center">
          <Volume2 className="w-4 h-4 mx-auto mb-1 text-blue-500" />
          <p className="text-xs text-gray-500 dark:text-gray-400">{t('voice.calibration.noiseLevel')}</p>
          <p className="text-sm font-bold text-gray-900 dark:text-white">{t(`voice.calibration.${noiseLevel || 'fair'}`)}</p>
        </div>
        <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3 text-center">
          <Shield className="w-4 h-4 mx-auto mb-1 text-emerald-500" />
          <p className="text-xs text-gray-500 dark:text-gray-400">{t('voice.calibration.quality')}</p>
          <p className={`text-sm font-bold ${qualityGateFailed ? 'text-red-500' : 'text-emerald-600 dark:text-emerald-400'}`}>
            {qualityGateFailed ? '✗' : '✓'}
          </p>
        </div>
      </div>

      {/* AGC: Domain breakdown */}
      {domains.size > 0 && (
        <div className="w-full max-w-sm mb-4">
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2">{t('voice.calibration.domainBreakdown')}</p>
          <div className="space-y-1.5">
            {[...domains.entries()].map(([domain, data]) => {
              const domainAcc = Math.round((1 - data.totalWER / data.count) * 100)
              return (
                <div key={domain} className="flex items-center gap-2">
                  <span className="text-xs text-gray-600 dark:text-gray-400 w-20 truncate">{domain}</span>
                  <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${domainAcc >= 80 ? 'bg-emerald-500' : domainAcc >= 60 ? 'bg-amber-500' : 'bg-red-500'}`}
                      style={{ width: `${domainAcc}%` }}
                    />
                  </div>
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-300 w-10 text-right">{domainAcc}%</span>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* AGC: Quality gate warning */}
      {qualityGateFailed && (
        <div className="w-full max-w-sm bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-3 mb-4">
          <p className="text-xs text-red-700 dark:text-red-300 font-medium">
            {t('voice.calibration.qualityGateFailed')}
          </p>
        </div>
      )}

      {/* AGC: Save result feedback */}
      {saveResult && (
        <div className={`w-full max-w-sm rounded-xl p-3 mb-4 ${
          saveResult.promoted
            ? 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800'
            : saveResult.comparison?.action === 'warn'
              ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800'
              : 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800'
        }`}>
          <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
            {saveResult.comparison?.reason}
          </p>
          {saveResult.comparison?.action === 'warn' && !saveResult.promoted && (
            <button
              onClick={onForcePromote}
              className="mt-2 text-xs text-amber-700 dark:text-amber-300 underline"
            >
              {t('voice.calibration.useNewAnyway')}
            </button>
          )}
        </div>
      )}

      {/* AGC: Action buttons */}
      {!saveResult ? (
        <button
          onClick={onSave}
          disabled={isSaving || qualityGateFailed}
          className={`w-full max-w-sm py-3 font-semibold rounded-xl flex items-center justify-center gap-2 transition-colors ${
            qualityGateFailed
              ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 cursor-not-allowed'
              : 'bg-purple-600 hover:bg-purple-700 text-white'
          }`}
        >
          {isSaving ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Save className="w-5 h-5" />
          )}
          {t('voice.calibration.saveButton')}
        </button>
      ) : (
        <button
          onClick={onComplete}
          className="w-full max-w-sm py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl flex items-center justify-center gap-2"
        >
          <CheckCircle2 className="w-5 h-5" /> {t('common.done')}
        </button>
      )}
    </motion.div>
  )
}

// ─────────────────────────────────────────────────────────
// AGC: Freeform confirmation test data
// AGC: 3 open-ended tests where user says their OWN command (not scripted)
// ─────────────────────────────────────────────────────────

const FREEFORM_TESTS = [
  {
    category: 'buy',
    icon: '🛒',
    color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    labelKey: 'voice.calibration.freeform.buyLabel',
    tipsKey: 'voice.calibration.freeform.buyTips',
  },
  {
    category: 'sell',
    icon: '💵',
    color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
    labelKey: 'voice.calibration.freeform.sellLabel',
    tipsKey: 'voice.calibration.freeform.sellTips',
  },
  {
    category: 'task',
    icon: '📋',
    color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    labelKey: 'voice.calibration.freeform.taskLabel',
    tipsKey: 'voice.calibration.freeform.taskTips',
  },
]

/** AGC: Step 3 — Freeform confirmation test */
const FreeformStep: React.FC<{
  testIndex: number
  isRecording: boolean
  isProcessing: boolean
  transcribedText: string | null
  showConfirmation: boolean
  onStartRecording: () => void
  onStopRecording: () => void
  onConfirm: () => void
  onReject: () => void
  onSkipAll?: () => void
}> = ({ testIndex, isRecording, isProcessing, transcribedText, showConfirmation, onStartRecording, onStopRecording, onConfirm, onReject, onSkipAll }) => {
  const { t } = useTranslation()
  const test = FREEFORM_TESTS[testIndex]
  if (!test) return null

  return (
    <motion.div
      key={`freeform-${testIndex}`}
      variants={slideVariants}
      initial="enter" animate="center" exit="exit"
      transition={{ duration: 0.3 }}
      className="flex flex-col items-center px-6 py-4"
    >
      {/* AGC: Progress */}
      <div className="w-full max-w-sm mb-3">
        <div className="flex justify-between items-center text-xs text-gray-500 dark:text-gray-400 mb-1">
          <span>{t('voice.calibration.freeform.step', { current: testIndex + 1, total: FREEFORM_TESTS.length })}</span>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${test.color}`}>
            {test.icon} {t(test.labelKey)}
          </span>
        </div>
        <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div
            className="h-full bg-orange-500 transition-all duration-300 rounded-full"
            style={{ width: `${((testIndex + (showConfirmation ? 1 : 0)) / FREEFORM_TESTS.length) * 100}%` }}
          />
        </div>
      </div>

      {/* AGC: Title */}
      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
        {t('voice.calibration.freeform.title')}
      </h3>

      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 text-center max-w-sm">
        {t('voice.calibration.freeform.subtitle')}
      </p>

      {/* AGC: Tips — what to include (NOT the text to say) */}
      <div className="w-full max-w-sm bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-3 mb-4">
        <p className="text-xs font-semibold text-amber-700 dark:text-amber-300 mb-1">
          {test.icon} {t(test.labelKey)}
        </p>
        <p className="text-xs text-amber-700 dark:text-amber-400 leading-relaxed whitespace-pre-line">
          {t(test.tipsKey)}
        </p>
      </div>

      {/* AGC: Confirmation modal simulation */}
      {showConfirmation && transcribedText ? (
        <div className="w-full max-w-sm">
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 mb-4 shadow-sm">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
              {t('voice.calibration.freeform.weHeard')}
            </p>
            <p className="text-sm font-medium text-gray-900 dark:text-white leading-relaxed mb-4">
              &ldquo;{transcribedText}&rdquo;
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              {t('voice.calibration.freeform.isCorrect')}
            </p>
            <div className="flex gap-3">
              <button
                onClick={onReject}
                className="flex-1 py-2.5 border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 font-semibold rounded-xl flex items-center justify-center gap-2"
              >
                <XCircle className="w-4 h-4" /> {t('voice.calibration.freeform.no')}
              </button>
              <button
                onClick={onConfirm}
                className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl flex items-center justify-center gap-2"
              >
                <CheckCircle2 className="w-4 h-4" /> {t('voice.calibration.freeform.yes')}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center">
          {isProcessing ? (
            <div className="w-20 h-20 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-orange-600 dark:text-orange-400 animate-spin" />
            </div>
          ) : (
            <>
              {isRecording && (
                <p className="text-sm font-semibold text-red-500 mb-2 animate-pulse">
                  {t('voice.calibration.tapToSend')}
                </p>
              )}
              <button
                onClick={isRecording ? onStopRecording : onStartRecording}
                className={`w-20 h-20 rounded-full flex items-center justify-center transition-all ${
                  isRecording
                    ? 'bg-red-500 scale-110 animate-pulse'
                    : 'bg-orange-500 hover:bg-orange-600 active:scale-95'
                }`}
              >
                {isRecording ? (
                  <MicOff className="w-8 h-8 text-white" />
                ) : (
                  <Mic className="w-8 h-8 text-white" />
                )}
              </button>
            </>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
            {isRecording
              ? t('voice.calibration.recording')
              : isProcessing
                ? t('voice.calibration.processing')
                : t('voice.calibration.tapToRecord')}
          </p>
        </div>
      )}

      {/* AGC: Skip all — only shown when not in confirmation modal */}
      {!showConfirmation && onSkipAll && (
        <button
          onClick={onSkipAll}
          className="mt-4 px-5 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-all"
        >
          {t('voice.calibration.skipAllButton')}
        </button>
      )}
    </motion.div>
  )
}

/** AGC: Tier gate — shown to FREE users */
const TierGateStep: React.FC<{ onSkip?: () => void; onBack?: () => void }> = ({ onSkip, onBack }) => {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col items-center px-6 py-8 text-center">
      <div className="w-20 h-20 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mb-6">
        <Zap className="w-10 h-10 text-amber-600 dark:text-amber-400" />
      </div>
      <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
        {t('voice.calibration.requiresPro')}
      </h2>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-6 max-w-sm">
        {t('voice.calibration.requiresProDescription')}
      </p>
      {onSkip && (
        <button
          onClick={onSkip}
          className="mt-2 px-8 py-3 text-base font-semibold text-gray-700 dark:text-gray-200 border-2 border-gray-300 dark:border-gray-500 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 hover:border-gray-400 transition-all"
        >
          {t('voice.calibration.skipButton')}
        </button>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────
// AGC: Main component
// ─────────────────────────────────────────────────────────

export const VoiceCalibration: React.FC<VoiceCalibrationProps> = ({
  onComplete,
  onSkip,
  onBack,
  isPostWizard = false,
}) => {
  const { t } = useTranslation()
  const { tier } = useSubscription()
  const cal = useVoiceCalibration()

  // AGC: Wizard step tracking
  type WizardStep = 'intro' | 'noise' | 'phrases' | 'freeform' | 'results'
  const [step, setStep] = useState<WizardStep>('intro')

  // AGC: Freeform test state
  const [freeformIndex, setFreeformIndex] = useState(0)
  const [freeformTranscript, setFreeformTranscript] = useState<string | null>(null)
  const [freeformShowConfirm, setFreeformShowConfirm] = useState(false)
  const [freeformConfirmed, setFreeformConfirmed] = useState(0)

  // AGC: Load phrases on mount
  useEffect(() => {
    cal.loadPhrases()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // AGC: Tier gate removed — all tiers can calibrate. Profile is saved but only
  // AGC: used when admin toggle agentChatEnhancedTranscription is ON + user has Pro/Premium.

  const handleStartCalibration = useCallback(async () => {
    setStep('noise')
  }, [])

  const handleNoiseComplete = useCallback(async () => {
    await cal.recordNoiseBaseline()
  }, [cal])

  // AGC: Auto-advance after noise baseline recorded
  useEffect(() => {
    if (cal.noiseBaseline && step === 'noise') {
      // AGC: Don't auto-advance, let user click Next
    }
  }, [cal.noiseBaseline, step])

  const handleNoiseNext = useCallback(() => {
    setStep('phrases')
  }, [])

  const handlePhraseNext = useCallback(() => {
    if (cal.currentPhraseIndex < cal.totalPhrases - 1) {
      cal.nextPhrase()
    } else {
      cal.nextPhrase()
      // AGC: Go to freeform confirmation tests before results
      setStep('freeform')
      setFreeformIndex(0)
      setFreeformTranscript(null)
      setFreeformShowConfirm(false)
      setFreeformConfirmed(0)
    }
  }, [cal])

  // AGC: Freeform test handlers — uses transcribeFreeform (NOT calibration phrase endpoint)
  // AGC: Freeform results do NOT affect the WER profile — they're confirmation tests only
  const handleFreeformStop = useCallback(async () => {
    const transcript = await cal.transcribeFreeform()
    if (transcript) {
      setFreeformTranscript(transcript)
      setFreeformShowConfirm(true)
    }
  }, [cal])

  const handleFreeformConfirm = useCallback(() => {
    const newConfirmed = freeformConfirmed + 1
    setFreeformConfirmed(newConfirmed)
    setFreeformShowConfirm(false)
    setFreeformTranscript(null)

    if (freeformIndex < FREEFORM_TESTS.length - 1) {
      setFreeformIndex(prev => prev + 1)
    } else {
      // AGC: All freeform tests done → go to results
      setStep('results')
    }
  }, [freeformConfirmed, freeformIndex])

  const handleFreeformReject = useCallback(() => {
    // AGC: Not a match — let user try again
    setFreeformShowConfirm(false)
    setFreeformTranscript(null)
  }, [])

  const handleSave = useCallback(async () => {
    await cal.saveProfile()
  }, [cal])

  const handleComplete = useCallback(() => {
    cal.cleanup()
    onComplete()
  }, [cal, onComplete])

  const handleSkip = useCallback(() => {
    cal.cleanup()
    onSkip?.()
  }, [cal, onSkip])

  // AGC: handleSkipAll — exits wizard from any mid-step, available when not post-wizard mandatory
  const handleSkipAll = useCallback(() => {
    cal.cleanup()
    onSkip?.()
  }, [cal, onSkip])

  const showResult = cal.state === 'phrase_result'

  return (
    <div className="min-h-screen bg-[#fafbfc] dark:bg-gray-900">
      {!isPostWizard && (
        <TopBar
          title={t('voice.calibration.title')}
          onBack={onBack || (step !== 'intro' ? () => {
            if (step === 'noise') setStep('intro')
            else if (step === 'phrases') setStep('noise')
            else if (step === 'freeform') setStep('phrases')
            else if (step === 'results') setStep('freeform')
          } : undefined)}
          className="bg-white dark:bg-slate-800 shadow-sm"
        />
      )}

      {isPostWizard && step !== 'intro' && (
        <div className="flex items-center px-4 pt-4">
          <button
            onClick={() => {
              if (step === 'noise') setStep('intro')
              else if (step === 'phrases') setStep('noise')
              else if (step === 'freeform') setStep('phrases')
              else if (step === 'results') setStep('freeform')
            }}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>
        </div>
      )}

      <div className="pb-24">
        <AnimatePresence mode="wait">
          {step === 'intro' && (
            <IntroStep
              key="intro"
              onStart={handleStartCalibration}
              onSkip={handleSkip}
              isPostWizard={isPostWizard}
              lastCalibration={cal.calibrationStatus?.calibratedAt || null}
            />
          )}

          {step === 'noise' && (
            <NoiseStep
              key="noise"
              isRecording={cal.state === 'recording_noise'}
              noiseLevel={cal.noiseBaseline?.snrLevel || null}
              onRecord={handleNoiseComplete}
              onNext={handleNoiseNext}
              onSkipAll={onSkip ? handleSkipAll : undefined}
            />
          )}

          {step === 'phrases' && (
            <PhraseStep
              key={`phrase-${cal.currentPhraseIndex}`}
              phrase={cal.currentPhrase}
              phraseIndex={cal.currentPhraseIndex}
              totalPhrases={cal.totalPhrases}
              isRecording={cal.state === 'recording_phrase'}
              isProcessing={cal.state === 'processing_phrase'}
              lastResult={cal.lastResult}
              showResult={showResult}
              onStartRecording={cal.startPhraseRecording}
              onStopRecording={cal.stopPhraseRecording}
              onNext={handlePhraseNext}
              onRetry={cal.retryPhrase}
              onSkipAll={onSkip ? handleSkipAll : undefined}
            />
          )}

          {step === 'freeform' && (
            <FreeformStep
              key={`freeform-${freeformIndex}`}
              testIndex={freeformIndex}
              isRecording={cal.state === 'recording_phrase'}
              isProcessing={cal.state === 'processing_phrase'}
              transcribedText={freeformTranscript}
              showConfirmation={freeformShowConfirm}
              onStartRecording={cal.startPhraseRecording}
              onStopRecording={handleFreeformStop}
              onConfirm={handleFreeformConfirm}
              onReject={handleFreeformReject}
              onSkipAll={onSkip ? handleSkipAll : undefined}
            />
          )}

          {step === 'results' && (
            <ResultsStep
              key="results"
              phraseResults={cal.phraseResults}
              acceptedCount={cal.acceptedCount}
              rejectedCount={cal.rejectedCount}
              noiseLevel={cal.noiseBaseline?.snrLevel || null}
              saveResult={cal.saveResult as any}
              isSaving={cal.state === 'saving'}
              onSave={handleSave}
              onForcePromote={cal.forcePromote}
              onComplete={handleComplete}
            />
          )}
        </AnimatePresence>

        {/* AGC: Error display */}
        {cal.error && (
          <div className="mx-6 mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
            <p className="text-xs text-red-700 dark:text-red-300">{cal.error}</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default VoiceCalibration
